'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useCalendarStore } from '@/stores/calendar-store';
import { LAYOUT_OPTIONS } from '@/lib/brand';
import { useScrollReveal } from './useScrollReveal';
import { MiniMonth } from '../calendar/MiniMonth';
import { PosterOverview } from '../calendar/PosterOverview';
import { SemesterWeekView } from '../calendar/SemesterWeekView';
import { LedgerShowcaseTimeline } from './LedgerShowcaseTimeline';
import { ListShowcase, ListMonthGrid } from './ListShowcase';
import { SmartHero } from './SmartHero';
import type { CalendarEvent, LensId } from '@/lib/types';
import type { LongWeekendDayType, LongWeekend } from '@/lib/long-weekends';
import { ACADEMIC_TYPE_LABELS, getEventColor, getLegendItems } from '@/lib/constants';
import { uiFb } from '@/lib/i18n';
import { getLocale, formatDateRangeCompact } from '@/lib/date-format';

function getShowcaseLegendItems(lang: string) {
  const items = getLegendItems(lang);
  return [
    { key: 'national', label: items.find(i => i.type === 'national_day')?.label ?? 'National', cssVar: 'var(--event-national)' },
    { key: 'holiday', label: items.find(i => i.type === 'public_holiday')?.label ?? 'Holiday', cssVar: 'var(--event-holiday)' },
    { key: 'festival', label: items.find(i => i.type === 'cultural_festival')?.label ?? 'Festival', cssVar: 'var(--event-festival)' },
    { key: 'event', label: items.find(i => i.type === 'local_event')?.label ?? 'Event', cssVar: 'var(--event-event)' },
    { key: 'observance', label: items.find(i => i.type === 'observance')?.label ?? 'Observance', cssVar: 'var(--event-observance)' },
  ];
}

const ACADEMIC_LEGEND_ITEMS = Object.entries(ACADEMIC_TYPE_LABELS).map(([key, label]) => ({
  key,
  label,
  cssVar: getEventColor(key),
}));

function getLongWeekendLegend(lang: string) {
  return [
    { key: 'holiday', label: uiFb('ui.lw_legend_holiday', 'Holiday', lang), cls: 'bg-semantic-success' },
    { key: 'bridge', label: uiFb('ui.lw_legend_take_leave', 'Take Leave', lang), cls: 'bg-semantic-warning' },
    { key: 'weekend', label: uiFb('ui.lw_legend_weekend', 'Weekend', lang), cls: 'bg-semantic-success/30' },
  ];
}

function getLensLabel(lens: LensId, lang: string): string {
  return uiFb(`ui.lens_${lens.replace('-', '_')}`, lens, lang);
}

const MONTH_DELAYS = ['delay-100', 'delay-200', 'delay-300', 'delay-400'];

function PreviewOverlay({ href, lang = 'en' }: { href?: string; lang?: string }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-center pb-6">
      <a
        href={href || '/today'}
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-text-on-brand shadow-lg transition-transform hover:scale-105"
      >
        {uiFb('ui.misc.see_full_calendar', 'See Full Calendar', lang)}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 3l5 5-5 5" />
        </svg>
      </a>
    </div>
  );
}

function formatBridgeDays(days: string[], lang: string): string {
  const locale = getLocale(lang);
  return days.map(d => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
  }).join(', ');
}

export function VacationList({ longWeekends, lang = 'en' }: { longWeekends: LongWeekend[]; lang?: string }) {
  if (longWeekends.length === 0) return null;
  return (
    <div className="mt-6 overflow-hidden content-card">
      <div className="flex items-center justify-between border-b border-border-subtle bg-surface-hover/50 px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-wider text-text-primary">{uiFb('ui.vacation_planner', 'Vacation Planner', lang)}</span>
        <span className="text-xs tabular-nums text-muted">{uiFb('ui.lw_count', '{count} long weekends', lang, { count: String(longWeekends.length) })}</span>
      </div>
      <div className="divide-y divide-border-subtle">
        {longWeekends.map((lw) => (
          <div key={lw.holiday.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <span className={`h-2 w-2 shrink-0 rounded-full ${lw.leaveDaysNeeded === 0 ? 'bg-semantic-success' : 'bg-semantic-warning'}`} />
            <span className="min-w-0 flex-1">
              <span className="font-medium text-text-primary">{lw.holiday.title}</span>
              <span className="ml-2 text-xs text-muted">{formatDateRangeCompact(lw.startDate, lw.endDate, lang)}</span>
            </span>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-caption font-semibold sm:text-label ${
              lw.leaveDaysNeeded === 0
                ? 'bg-semantic-success/10 text-semantic-success'
                : 'bg-semantic-warning/10 text-semantic-warning'
            }`}>
              {lw.leaveDaysNeeded === 0
                ? uiFb('ui.lw_days_off', '{count} days off', lang, { count: String(lw.totalDaysOff) })
                : uiFb('ui.lw_take_get', 'Take {take}, get {get} off', lang, { take: String(lw.leaveDaysNeeded), get: String(lw.totalDaysOff) })}
            </span>
            {lw.bridgeDays.length > 0 && (
              <span className="hidden shrink-0 text-label text-muted sm:inline">
                {uiFb('ui.lw_leave_label', 'Leave:', lang)} {formatBridgeDays(lw.bridgeDays, lang)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface LandingShowcaseProps {
  entityIcon: string;
  entityLabel: string;
  year: number;
  events: CalendarEvent[];
  lens?: LensId;
  lang?: string;
  highlightDates?: Map<string, LongWeekendDayType> | null;
  longWeekends?: LongWeekend[] | null;
  hideHeader?: boolean;
  preview?: boolean;
  previewHref?: string;
  eventSlugMap?: Record<string, string>;
  countryCode?: string;
  lensLabelOverride?: string;
}

export function InlineLayoutPicker() {
  const { calendarLayout, setCalendarLayout, lang } = useCalendarStore();
  return (
    <div className="mb-4 flex items-center justify-center gap-2">
      <span className="text-xs font-medium text-muted">{uiFb('ui.view_label', 'View:', lang)}</span>
      <div className="flex items-center gap-1 rounded-lg border border-border-primary bg-surface-elevated p-0.5">
        {LAYOUT_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() => setCalendarLayout(option.id)}
            aria-label={uiFb(`ui.switch_view`, `Switch to ${option.label} view`, lang, { label: option.label })}
            aria-pressed={calendarLayout === option.id}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
              calendarLayout === option.id
                ? 'bg-brand text-text-on-brand shadow-sm'
                : 'text-muted hover:bg-surface-hover hover:text-text-secondary'
            }`}
          >
            {uiFb(`ui.view_${option.id}`, option.label, lang)}
          </button>
        ))}
      </div>
    </div>
  );
}

function PosterImageActions({ src, label, lang = 'en' }: { src: string; label: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== 'undefined' && navigator.clipboard?.write;

  async function handleCopy() {
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      // clipboard.write needs image/png — convert if webp
      let pngBlob = blob;
      if (blob.type !== 'image/png') {
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width;
        canvas.height = bmp.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(bmp, 0, 0);
        pngBlob = await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail — user can still right-click
    }
  }

  return (
    <div className="mt-3 flex items-center justify-center gap-3">
      {canCopy && (
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 content-card-elevated px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover"
        >
          {copied ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {uiFb('ui.copied', 'Copied!', lang)}
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
              {uiFb('ui.copy_image', 'Copy Image', lang)}
            </>
          )}
        </button>
      )}
      <a
        href={src}
        download={`${label.replace(/\s+/g, '-').toLowerCase()}-calendar.webp`}
        className="inline-flex items-center gap-1.5 content-card-elevated px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
        {uiFb('ui.save_image', 'Save Image', lang)}
      </a>
    </div>
  );
}

export function LandingShowcase({ entityIcon, entityLabel, year, events, lens = 'country', lang = 'en', highlightDates, longWeekends, hideHeader, preview, previewHref, eventSlugMap, countryCode, lensLabelOverride }: LandingShowcaseProps) {
  const { calendarLayout } = useCalendarStore();
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [posterImgError, setPosterImgError] = useState(false);
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();
  const { ref: gridRef1, revealed: gridRevealed1 } = useScrollReveal();
  const { ref: gridRef2, revealed: gridRevealed2 } = useScrollReveal();
  const { ref: gridRef3, revealed: gridRevealed3 } = useScrollReveal();

  const showToggle = lens === 'academic' && calendarLayout !== 'poster';
  // Academic pages span two calendar years, so the showcase heading must use the
  // academic-year range (e.g. 2026/2027), not a single calendar year (almanak-m465).
  const calendarHeading = lens === 'academic'
    ? uiFb('ui.calendar.academic_calendar', 'Academic Calendar {range}', lang, { range: `${year}/${year + 1}` })
    : uiFb('ui.misc.complete_calendar', 'Complete Calendar {year}', lang, { year });
  const sectionPy = hideHeader
    ? 'pt-4 pb-10 md:pt-6 md:pb-16'
    : 'py-10 md:py-16';

  if (calendarLayout === 'hybrid') {
    if (preview) {
      return (
        <section id="calendar" className={`scroll-mt-20 ${sectionPy}`}>
          <div className="relative mx-auto w-full max-w-6xl overflow-hidden" style={{ maxHeight: '28rem' }}>
            <SmartHero events={events} year={year} lens={lens} lang={lang} longWeekends={longWeekends} />
            <div className="px-4 md:px-8 lg:px-16 xl:px-20">
              <ListMonthGrid events={events} lang={lang} />
            </div>
            <PreviewOverlay href={previewHref} lang={lang} />
          </div>
        </section>
      );
    }
    return (
      <section id="calendar" className={`scroll-mt-20 ${sectionPy}`}>
        <div className="mx-auto w-full max-w-6xl">
          {!hideHeader && (
            <div className="mb-8 px-4 text-center md:px-8 lg:px-16 xl:px-20">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl">
                {entityIcon} {entityLabel} — {calendarHeading}
              </h2>
              <p className="mt-2 text-sm text-text-secondary md:text-base">
                {uiFb('ui.event.summary', '{count} events', lang, { count: events.length })}
              </p>
            </div>
          )}

          <SmartHero events={events} year={year} lens={lens} lang={lang} longWeekends={longWeekends} />

          <div className="px-4 md:px-8 lg:px-16 xl:px-20">
            <ListMonthGrid events={events} lang={lang} />

            {longWeekends && longWeekends.length > 0 && (
              <VacationList longWeekends={longWeekends} lang={lang} />
            )}
          </div>
        </div>
      </section>
    );
  }

  if (calendarLayout === 'list') {
    if (preview) {
      return (
        <section id="calendar" className={`scroll-mt-20 px-4 ${sectionPy} md:px-8 lg:px-16 xl:px-20`}>
          <div className="relative mx-auto w-full max-w-6xl overflow-hidden" style={{ maxHeight: '28rem' }}>
            <ListShowcase
              events={events}
              year={year}
              entityLabel={entityLabel}
              entityIcon={entityIcon}
              lens={lens}
              lang={lang}
              longWeekends={longWeekends}
              hideHeader={hideHeader}
            />
            <PreviewOverlay href={previewHref} lang={lang} />
          </div>
        </section>
      );
    }
    return (
      <ListShowcase
        events={events}
        year={year}
        entityLabel={entityLabel}
        entityIcon={entityIcon}
        lens={lens}
        lang={lang}
        longWeekends={longWeekends}
        hideHeader={hideHeader}
      />
    );
  }

  if (calendarLayout === 'poster') {
    // Show pre-rendered poster image for country lens (non-preview), fallback to DOM
    const posterSlug: Record<string, string> = {
      AE: 'united-arab-emirates-holidays', AF: 'afghanistan-holidays',
      EG: 'egypt-holidays', ET: 'ethiopia-holidays',
      ID: 'indonesia-holidays', IN: 'india-holidays',
    };
    const posterSrc = countryCode && lens === 'country' && !preview && posterSlug[countryCode]
      ? `/posters/calendar/${posterSlug[countryCode]}.webp`
      : null;

    return (
      <section id="calendar" className={`scroll-mt-20 px-4 ${sectionPy} md:px-8 lg:px-16 xl:px-20`}>
        <div className={`mx-auto w-full max-w-6xl${preview ? ' relative overflow-hidden' : ''}`} style={preview ? { maxHeight: '32rem' } : undefined}>
          {posterSrc && !posterImgError ? (
            <div className="flex flex-col items-center">
              <Image
                src={posterSrc}
                alt={`${entityLabel} ${year} Holiday Calendar Poster`}
                width={0}
                height={0}
                sizes="100vw"
                className="h-auto w-full rounded-2xl"
                onError={() => setPosterImgError(true)}
              />
              <PosterImageActions src={posterSrc} label={entityLabel} lang={lang} />
            </div>
          ) : (
            <PosterOverview
              events={events}
              year={year}
              lensLabel={lensLabelOverride ?? getLensLabel(lens, lang)}
              entityLabel={entityLabel}
              entityIcon={entityIcon}
              lens={lens}
              lang={lang}
            />
          )}

          {/* Vacation recommendation cards */}
          {!preview && longWeekends && longWeekends.length > 0 && (
            <VacationList longWeekends={longWeekends} lang={lang} />
          )}
          {preview && <PreviewOverlay href={previewHref} lang={lang} />}
        </div>
      </section>
    );
  }

  if (calendarLayout === 'timeline') {
    return (
      <section id="calendar" className={`scroll-mt-20 px-4 ${sectionPy} md:px-8 lg:px-16 xl:px-20`}>
        <div className={`mx-auto w-full max-w-6xl${preview ? ' relative overflow-hidden' : ''}`} style={preview ? { maxHeight: '32rem' } : undefined}>
          {/* Section header */}
          {!hideHeader && (
            <div
              ref={headerRef}
              className={`scroll-reveal ${headerRevealed ? 'revealed' : ''} mb-8 text-center`}
            >
              <h2 className="font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl">
                {entityIcon} {entityLabel} — {calendarHeading}
              </h2>
              <p className="mt-2 text-sm text-text-secondary md:text-base">
                {uiFb('ui.event.summary', '{count} events', lang, { count: events.length })}
              </p>
            </div>
          )}

          {/* Month/Week toggle for academic lens */}
          {!preview && showToggle && (
            <div className={`${hideHeader ? '' : 'mt-4 '}mb-6 flex items-center justify-center gap-1`}>
              <button
                onClick={() => setViewMode('month')}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === 'month'
                    ? 'bg-brand text-text-on-brand'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {uiFb('ui.month_view', 'Month View', lang)}
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  viewMode === 'week'
                    ? 'bg-brand text-text-on-brand'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {uiFb('ui.week_view', 'Week View', lang)}
              </button>
            </div>
          )}

          {/* Content */}
          {!preview && showToggle && viewMode === 'week' ? (
            <SemesterWeekView events={events} year={year} lang={lang} />
          ) : (
            <LedgerShowcaseTimeline events={events} year={year} lang={lang} lens={lens} />
          )}

          {/* Legend */}
          {!preview && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {(lens === 'academic' ? ACADEMIC_LEGEND_ITEMS : getShowcaseLegendItems(lang)).map((item) => (
                <span
                  key={item.key}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-card px-2.5 py-1 text-label font-semibold text-text-secondary md:text-xs"
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: item.cssVar }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
          )}

          {/* Vacation recommendation cards */}
          {!preview && longWeekends && longWeekends.length > 0 && (
            <VacationList longWeekends={longWeekends} lang={lang} />
          )}
          {preview && <PreviewOverlay href={previewHref} lang={lang} />}
        </div>
      </section>
    );
  }

  return (
    <section id="calendar" className={`scroll-mt-20 px-4 ${sectionPy} md:px-8 lg:px-16 xl:px-20`}>
      <div className="mx-auto w-full max-w-6xl">
        {/* Section header */}
        {!hideHeader && (
          <div
            ref={headerRef}
            className={`scroll-reveal ${headerRevealed ? 'revealed' : ''} mb-8 text-center`}
          >
            <h2 className="font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl">
              {entityIcon} {entityLabel} — {calendarHeading}
            </h2>
            <p className="mt-2 text-sm text-text-secondary md:text-base">
              {uiFb('ui.event.summary', '{count} events', lang, { count: events.length })}
            </p>
          </div>
        )}

        {/* Month/Week toggle for academic lens */}
        {showToggle && (
          <div className={`${hideHeader ? '' : 'mt-4 '}mb-6 flex items-center justify-center gap-1`}>
            <button
              onClick={() => setViewMode('month')}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                viewMode === 'month'
                  ? 'bg-brand text-text-on-brand'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {uiFb('ui.month_view', 'Month View', lang)}
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                viewMode === 'week'
                  ? 'bg-brand text-text-on-brand'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {uiFb('ui.week_view', 'Week View', lang)}
            </button>
          </div>
        )}

        {/* Week view for academic */}
        {!preview && showToggle && viewMode === 'week' ? (
          <SemesterWeekView events={events} year={year} lang={lang} />
        ) : (
        /* Month grid */
        <div className={`relative${preview ? ' overflow-hidden' : ''}`}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4">
            {/* Row 1 (months 0-3) */}
            <div ref={gridRef1} className="col-span-2 grid grid-cols-2 gap-3 sm:col-span-3 sm:grid-cols-3 md:col-span-4 md:grid-cols-4 md:gap-4">
              {[0, 1, 2, 3].map((m, i) => (
                <div key={m} className={`scroll-reveal ${gridRevealed1 ? 'revealed' : ''} ${MONTH_DELAYS[i]}`}>
                  <MiniMonth year={year} month={m} events={events} lang={lang} highlightDates={highlightDates} eventSlugMap={eventSlugMap} />
                </div>
              ))}
            </div>

            {/* Row 2 (months 4-7) */}
            <div ref={gridRef2} className="col-span-2 grid grid-cols-2 gap-3 sm:col-span-3 sm:grid-cols-3 md:col-span-4 md:grid-cols-4 md:gap-4">
              {[4, 5, 6, 7].map((m, i) => (
                <div key={m} className={`scroll-reveal ${gridRevealed2 ? 'revealed' : ''} ${MONTH_DELAYS[i]}`}>
                  <MiniMonth year={year} month={m} events={events} lang={lang} highlightDates={highlightDates} eventSlugMap={eventSlugMap} />
                </div>
              ))}
            </div>

            {/* Row 3 (months 8-11) — hidden in preview */}
            {!preview && (
              <div ref={gridRef3} className="col-span-2 grid grid-cols-2 gap-3 sm:col-span-3 sm:grid-cols-3 md:col-span-4 md:grid-cols-4 md:gap-4">
                {[8, 9, 10, 11].map((m, i) => (
                  <div key={m} className={`scroll-reveal ${gridRevealed3 ? 'revealed' : ''} ${MONTH_DELAYS[i]}`}>
                    <MiniMonth year={year} month={m} events={events} lang={lang} highlightDates={highlightDates} eventSlugMap={eventSlugMap} />
                  </div>
                ))}
              </div>
            )}
          </div>
          {preview && <PreviewOverlay href={previewHref} lang={lang} />}
        </div>
        )}

        {/* Legend */}
        {!preview && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {highlightDates ? (
              getLongWeekendLegend(lang).map((item) => (
                <span key={item.key} className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-card px-2.5 py-1 text-label font-semibold text-text-secondary md:text-xs">
                  <span className={`inline-block h-2.5 w-2.5 rounded ${item.cls}`} />
                  {item.label}
                </span>
              ))
            ) : (
              (lens === 'academic' ? ACADEMIC_LEGEND_ITEMS : getShowcaseLegendItems(lang)).map((item) => (
                <span key={item.key} className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-card px-2.5 py-1 text-label font-semibold text-text-secondary md:text-xs">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: item.cssVar }} />
                  {item.label}
                </span>
              ))
            )}
          </div>
        )}

        {/* Vacation recommendation cards */}
        {!preview && longWeekends && longWeekends.length > 0 && (
          <VacationList longWeekends={longWeekends} lang={lang} />
        )}
      </div>
    </section>
  );
}
